import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

// ─── Admin QR Scanner Component ──────────────────────────────────────────────
const AdminQrScanner = ({ onScanSuccess }) => {
  const html5QrCodeRef = useRef(null);
  const isMountedRef = useRef(true);
  const isScanningRef = useRef(false);
  const [lastResult, setLastResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [scanFileError, setScanFileError] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;
    isScanningRef.current = false;

    const initTimer = setTimeout(async () => {
      if (!isMountedRef.current) return;
      if (!document.getElementById("qr-reader")) return;

      const qr = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = qr;

      try {
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => {
            if (!isMountedRef.current || isScanningRef.current) return;
            isScanningRef.current = true;
            try { qr.pause(); } catch (_) {}
            setLastResult(decodedText);
            onScanSuccess(decodedText);
          },
          () => {} // ignore per-frame errors silently
        );
      } catch (err) {
        if (isMountedRef.current) {
          setCameraError("Could not access camera. Please grant camera permissions and try again.");
        }
      }
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initTimer);

      const qr = html5QrCodeRef.current;
      html5QrCodeRef.current = null;

      if (!qr) return;

      // Defensively stop then clear — catch every possible error silently
      const cleanup = async () => {
        try {
          await qr.stop();
        } catch (_) {
          // "Cannot stop, scanner is not running or paused" — safe to ignore
        }
        try {
          qr.clear();
        } catch (_) {}
      };

      cleanup();
    };
  }, []); // eslint-disable-line

  const handleRescan = () => {
    setLastResult(null);
    setScanFileError(null);
    isScanningRef.current = false;
    try {
      html5QrCodeRef.current?.resume();
    } catch (_) {}
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanFileError(null);

    const fileQr = new Html5Qrcode("qr-reader-file");
    fileQr
      .scanFile(file, true)
      .then((decodedText) => {
        setLastResult(decodedText);
        onScanSuccess(decodedText);
      })
      .catch(() => {
        setScanFileError("Could not find a valid QR code in that image.");
      })
      .finally(() => {
        try { fileQr.clear(); } catch (_) {}
      });
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="text-5xl mb-2">📷</div>
        <h3 className="text-xl font-black text-gray-800">Pet QR Scanner</h3>
        <p className="text-sm text-gray-500 font-medium">
          Point the camera at a pet's QR code to instantly pull up their profile.
        </p>
      </div>

      {/* Scanner box */}
      <div className="flex justify-center">
        <div className="w-full max-w-sm bg-black rounded-3xl shadow-lg border-4 border-purple-100 overflow-hidden relative min-h-[300px] flex items-center justify-center">
          {cameraError ? (
            <div className="text-center px-6 space-y-4">
              <p className="text-red-400 font-bold">{cameraError}</p>
            </div>
          ) : (
            <div id="qr-reader" className="w-full h-full" />
          )}
        </div>
      </div>

      {/* Hidden div used purely for parsing image files */}
      <div id="qr-reader-file" className="hidden" />

      {/* Manual Image Upload */}
      <div className="flex flex-col items-center justify-center mt-4">
        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">- OR -</p>
        <label className="cursor-pointer bg-white border-2 border-purple-200 text-purple-600 font-bold py-2 px-6 rounded-xl hover:bg-purple-50 transition-colors shadow-sm text-sm flex items-center gap-2">
          <span>📁</span> Upload QR Image
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileUpload}
          />
        </label>
        {scanFileError && <p className="text-red-500 text-xs mt-2 font-bold">{scanFileError}</p>}
      </div>

      {lastResult && (
        <div className="flex justify-center pt-4 border-t border-gray-100">
          <button
            onClick={handleRescan}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-2xl shadow hover:scale-105 transition-all text-sm"
          >
            🔄 Scan Another Pet
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminQrScanner;
